import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Grid } from '@mui/material';
import DateFieldWrapper from './DateFieldWrapper';
import { InputV2, AutocompleteV, HeaderSection } from 'components';
import { useDebounce } from 'hooks';

/* ================= VALIDATION ================= */

const checkFieldTypeValidation = (type) => {
  let validation = [];
  switch (type) {
    case 'number':
    case 'float':
      validation = ['isNumeric'];
      break;
    case 'textarea':
      validation = ['isLt|400'];
      break;
    default:
      break;
  }
  return validation;
};

/* ================= TEXT INPUT COMPONENT ================= */

const TextFieldItem = ({ item, methods, handleFieldValueChange }) => {
  const isTextField = item?.type === 'text';

  // مقدار اولیه را از selectedValue یا React Hook Form می‌گیریم
  const [localValue, setLocalValue] = useState(
    item?.selectedValue || methods.getValues(item?.name) || ''
  );

  const isTypingRef = useRef(false);

  // دیبانس فقط برای فیلد متن (text) اعمال می‌شود
  const debouncedValue = useDebounce(localValue, isTextField ? 500 : 0);

  const handleChange = (e) => {
    const value = e.target.value; // حذف trim از اینجا برای اینکه کاربر بتواند Space بزند

    if (isTextField) {
      isTypingRef.current = true;
      setLocalValue(value);
    } else {
      // برای textarea یا number بلافاصله ست می‌شود
      const finalValue = typeof value === 'string' ? value.trim() : value;
      methods.setValue(item.name, finalValue);
      handleFieldValueChange(item.id, finalValue, 'input');
    }
  };

  // همگام‌سازی مقدار محلی با تغییرات والد (مثلاً وقتی فرم ریست می‌شود)
  useEffect(() => {
    if (!isTypingRef.current) {
      setLocalValue(item?.selectedValue || '');
    }
  }, [item?.selectedValue]);

  // وقتی دیبانس تمام شد، مقدار به والد ارسال می‌شود
  useEffect(() => {
    if (!isTextField) return;

    // فقط اگر واقعاً تغییری ایجاد شده باشد به والد اطلاع می‌دهیم
    if (debouncedValue !== item?.selectedValue) {
      const finalValue = typeof debouncedValue === 'string' ? debouncedValue.trim() : debouncedValue;

      methods.setValue(item?.name, finalValue);
      handleFieldValueChange(item.id, finalValue, 'input');
    }

    isTypingRef.current = false;
  }, [debouncedValue]);

  const rules = item?.required
    ? ['required', ...checkFieldTypeValidation(item?.type)]
    : checkFieldTypeValidation(item?.type);

  return (
    <Grid item md={4} xs={12}>
      <InputV2
        errors={methods.formState.errors}
        register={methods.register}
        fullWidth
        label={item.persianName}
        value={localValue}
        onChangeValue={handleChange}
        name={item?.name}
        multiline={item?.type === 'textarea'}
        rows={item?.type === 'textarea' ? 5 : undefined}
        rules={rules}
      />
    </Grid>
  );
};

/* ================= MAIN COMPONENT ================= */

const GenerateDataField = (props) => {
  const { fields, methods, handleFieldValueChange = () => { } } = props;

  const renderSelectField = (item) => {
    return (
      <Grid item xs={12} md={4} style={{ position: 'relative' }} key={item.id}>
        <AutocompleteV
          control={methods.control}
          errors={methods.formState.errors}
          register={methods.register}
          rules={item?.required ? ['required'] : []}
          name={item?.name}
          placeholder="جستجو و انتخاب کنید..."
          label={item?.persianName}
          list={item?.fields}
          forceValue={item?.selectedValue || null}
          onChangeValue={(selectedOption) => {
            handleFieldValueChange(item.id, selectedOption, 'select');
          }}
          preventAddCustomOption
          disableClearable
        />
      </Grid>
    );
  };

  const renderDateField = (item) => {
    return (
      <DateFieldWrapper
        key={item.id}
        item={item}
        methods={methods}
        handleFieldValueChange={handleFieldValueChange}
      />
    );
  };

  const renderInputField = (item) => {
    return (
      <TextFieldItem
        key={item.id}
        item={item}
        methods={methods}
        handleFieldValueChange={handleFieldValueChange}
      />
    );
  };


  const sortedVisibleFields = useMemo(() => {
    return fields
      .filter((f) => f.visible !== false)
      .slice()
      .sort((a, b) => {
        const getPriority = (field) => {
          if (field.required && !field.conditionalRequired) return 1;
          if (field.conditionalRequired) return 2;
          return 3;
        };

        const priorityDiff = getPriority(a) - getPriority(b);

        if (priorityDiff !== 0) return priorityDiff;

        if (a.type === 'textarea' && b.type !== 'textarea') return 1;
        if (b.type === 'textarea' && a.type !== 'textarea') return -1;

        return 0;
      });
  }, [fields]);
  return (
    <>
      <Grid item xs={12}>
        <HeaderSection />
      </Grid>
      {sortedVisibleFields.map((item) => {
        const type = item?.type;

        if (type === 'radio') {
          return renderSelectField(item);
        } else if (type === 'date') {
          return renderDateField(item);
        } else {
          return renderInputField(item);
        }
      })}
      <Grid item xs={12}>
        <HeaderSection />
      </Grid>
    </>
  );
};

export default GenerateDataField;
